import React, { useCallback, useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { Activity, Filter, RefreshCw, User2, ShieldCheck, FileText, Trash2, Plus, Pencil, LogIn, Send, Check, X } from "lucide-react";

const ACTION_ICON = {
  "auth.login": LogIn,
  "quote.created": FileText,
  "quote.shared": Send,
  "quote.approved": Check,
  "quote.rejected": X,
  "quote.converted": FileText,
  "file.uploaded": Plus,
  "file.deleted": Trash2,
  "renewals.manual_run": RefreshCw,
};

const ACTION_TONE = {
  "auth.login": "sky",
  "file.uploaded": "emerald",
  "file.deleted": "rose",
  "quote.created": "blue",
  "quote.shared": "blue",
  "quote.approved": "emerald",
  "quote.rejected": "rose",
  "renewals.manual_run": "amber",
};

export default function ActivityLog() {
  const [rows, setRows] = useState([]);
  const [entityFilter, setEntityFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/activity", {
        params: entityFilter ? { entity_type: entityFilter, limit: 200 } : { limit: 200 },
      });
      setRows(data || []);
    } catch (error) {
      console.error("Failed to load activity log:", error);
    } finally {
      setLoading(false);
    }
  }, [entityFilter]);
  useEffect(() => { load(); }, [load]);

  const entities = Array.from(new Set(rows.map((r) => r.entity_type).filter(Boolean)));

  return (
    <DashboardLayout
      title="Activity Log"
      subtitle="Every user action across your workspace — audit-ready"
      right={
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 h-9 px-3 rounded-md border border-slate-700 bg-slate-900/60 text-xs text-slate-300">
            <Filter className="w-3.5 h-3.5" />
            <select
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
              data-testid="activity-entity-filter"
              className="bg-transparent outline-none"
            >
              <option value="">All entities</option>
              {entities.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
          <button
            data-testid="activity-refresh"
            onClick={load}
            className="btn-scale h-9 px-3 rounded-md border border-slate-700 text-slate-300 text-sm inline-flex items-center gap-2 hover:bg-slate-800"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      }
    >
      <div className="rounded-xl border border-zinc-800 overflow-hidden" data-testid="activity-table">
        <div className="max-h-[70vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-zinc-950 z-10">
              <tr className="text-left text-xs text-zinc-500 border-b border-zinc-800">
                <th className="p-3">When</th>
                <th className="p-3">User</th>
                <th className="p-3">Action</th>
                <th className="p-3">Entity</th>
                <th className="p-3">Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const Icon = ACTION_ICON[r.action] || Activity;
                const tone = ACTION_TONE[r.action] || "slate";
                return (
                  <tr key={r.activity_id} className="border-b border-zinc-800 hover:bg-zinc-900/40" data-testid={`activity-row-${r.activity_id}`}>
                    <td className="p-3 text-zinc-500 text-xs whitespace-nowrap">
                      {(r.at || "").slice(0, 19).replace("T", " ")}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2 text-xs">
                        <div className="w-6 h-6 rounded-full bg-blue-500/10 text-blue-400 grid place-items-center shrink-0">
                          <User2 className="w-3 h-3" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-zinc-200 truncate">{r.user_email || "system"}</div>
                          <div className="text-[10px] text-zinc-500">{r.user_role}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-md bg-${tone}-500/10 text-${tone}-400`}>
                        <Icon className="w-3 h-3" />
                        <span className="font-mono">{r.action}</span>
                      </span>
                    </td>
                    <td className="p-3 text-zinc-400 text-xs">
                      {r.entity_type ? <><span className="text-zinc-500">{r.entity_type}</span>{r.entity_id && <> · <span className="font-mono text-zinc-500">{r.entity_id.slice(0, 10)}…</span></>}</> : "—"}
                    </td>
                    <td className="p-3 text-zinc-500 text-xs max-w-[420px] truncate">
                      {r.meta && Object.keys(r.meta).length > 0 ? JSON.stringify(r.meta) : ""}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && !loading && (
                <tr><td colSpan={5} className="p-8 text-center text-zinc-500">No activity yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
